import { useEffect } from 'react'

import { Button, Icon, Loader } from '@paysense-india-services/elevate-web'

import { DOCUMENTS_API } from '@/constants/api'

import { useFetch } from '@/hooks/useFetch'

import ErrorPage from '@/components/ErrorPage'

import { buildDocumentRequestUrl, getDocIcon, resolveDocumentUrlFromResponse } from './utils'

import styles from './index.module.scss'

/**
 * Shared loan-documents list (loan-docs-info API). Used on /documents and as a
 * dashboard fallback when repayment-dashboard has no section items.
 */
const LoanDocumentsPanel = ({ showGreeting = true }) => {
  const { loading, error, data, apiRequest: fetchDocuments } = useFetch(DOCUMENTS_API)
  const { loading: documentLinkLoading, apiRequest: fetchDocumentOpenUrl } = useFetch(null)

  useEffect(() => {
    fetchDocuments({})
  }, [fetchDocuments])

  const handleDocumentClick = async doc => {
    const path = doc?.url
    if (!path || typeof path !== 'string') {
      return
    }

    const requestUrl = buildDocumentRequestUrl(path)
    if (!requestUrl) {
      return
    }

    const response = await fetchDocumentOpenUrl({ url: requestUrl })
    if (response === undefined) {
      return
    }

    const targetUrl = resolveDocumentUrlFromResponse(response)
    if (targetUrl) {
      window.open(targetUrl, '_blank', 'noopener,noreferrer')
    } else {
      showSnackbar('Document link not available', { type: 'error' })
    }
  }

  if (loading) {
    return <Loader color='brand' loadingText='Getting Documents...' pageLoader size='large' />
  }

  if (error) {
    return <ErrorPage />
  }

  const loanGroups = data?.doc_details ?? []
  const hasLoans = loanGroups.length > 0

  return (
    <>
      {documentLinkLoading ? (
        <div className={styles.docLinkLoaderOverlay}>
          <Loader color='brand' loadingText='Opening document...' pageLoader size='large' />
        </div>
      ) : null}

      {showGreeting ? (
        <div className='ew-d-flex ew-align-items-center ew-mt-md-24'>
          <p>
            <span className='ew-typography-large ew-fw-bold'>Hi, {data?.user_name}</span>
            <br />
            <span className='ew-typography-tiny ew-color-secondary ew-fw-regular'>
              {data?.phone_number}
            </span>
          </p>
        </div>
      ) : null}

      <div className={`${styles.loanGroups} ew-mt-24 ew-mb-24`}>
        {hasLoans ? (
          loanGroups.map(loan => (
            <section className={styles.loanSection} key={loan.provide_loan_id}>
              <div className={styles.loanSectionMeta}>
                <p className='ew-typography-normal ew-fw-bold ew-color-secondary'>
                  Loan Account Number
                </p>
                <p className='ew-typography-small ew-fw-regular ew-color-secondary'>
                  #{loan.provide_loan_id}
                </p>
              </div>
              <div className={styles.documentCard}>
                {loan.documents?.length ? (
                  loan.documents.map(doc => (
                    <div className={styles.content} key={`${loan.provide_loan_id}-${doc.doc_type}`}>
                      <div className={styles.icon}>
                        <img
                          alt={doc.display_name}
                          height={24}
                          src={getDocIcon(doc.doc_type)}
                          width={24}
                        />
                      </div>
                      <h3
                        className={`ew-typography-small ew-fw-regular ${styles.docLabel}`}
                        title={doc.display_name}>
                        {doc.display_name}
                      </h3>
                      <Button
                        className={`ew-ml-auto ${styles.chevronButton}`}
                        data-btn-tag-id={`doc-open-${doc.doc_type}`}
                        onClick={() => handleDocumentClick(doc)}
                        size='medium'
                        type='icon'
                        variant='secondary'>
                        <Icon fill='#171717' name='rightArrowHead' size={14} />
                      </Button>
                    </div>
                  ))
                ) : (
                  <div className={styles.emptyCard}>No documents for this account</div>
                )}
              </div>
            </section>
          ))
        ) : (
          <div className={styles.documentCard}>
            <div className={styles.emptyCard}>No Documents</div>
          </div>
        )}
      </div>
    </>
  )
}

export default LoanDocumentsPanel
